import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageSquare, Loader2, AlertCircle, ArrowRight, Check } from 'lucide-react';

// Flyer landing page (QR code → /halloween). A LEAD form, not checkout: no
// payment and no promo-code field. TREAT20 is honored by hand when Akeem calls
// or texts the lead back — it is deliberately NOT part of checkout promo logic.
//
// Claims close Sun Nov 1, 2026, 11:59:59 PM ET (EST — clocks fall back at 2 AM
// that morning). Keep in sync with HALLOWEEN_CLOSE_MS in api/ai.js, which also
// refuses submissions after this moment.
const HALLOWEEN_CLOSE = new Date('2026-11-01T23:59:59-05:00');
const SHOP_PHONE_DISPLAY = '(301) 885-9767';
const SHOP_PHONE_TEL = '+13018859767';

const GOLD = '#D4AF37';
const ORANGE = '#FF7A1A';

const VEHICLES = [
  { id: 'car', label: 'Car' },
  { id: 'suv', label: 'SUV' },
  { id: 'truck', label: 'Truck' },
];
// Plain-English choices — most people scanning a flyer don't know the package
// names, so they pick what their car needs and Akeem matches it to a package.
// Optional: defaults to "Not sure".
const NEEDS = [
  { id: 'average', title: 'Quick refresh', desc: 'Vacuum, wipe-down & windows' },
  { id: 'advanced', title: 'Full interior clean', desc: 'Everything wiped, crevices, light stains, UV protect' },
  { id: 'alchemist', title: 'Deep clean + shampoo', desc: 'Steam & seat/carpet shampoo for stains, spills, kid & pet messes' },
  { id: 'unsure', title: 'Not sure — help me pick', desc: 'Akeem will recommend one when he reaches out' },
];

// "Book now" from the thank-you screen → the regular booking wizard with the
// offer already applied: TREAT20 (20% off the package + 50% off the car seat),
// their vehicle size, the car seat add-on if they said yes, and their name and
// phone pre-filled. Maps the form's choices to booking-wizard ids.
const WIZARD_PACKAGE = { average: 'maintenance_refresh', advanced: 'interior', alchemist: 'alchemist' };
const WIZARD_VEHICLE = { car: 'sedan', suv: 'suv', truck: 'truck' };
const bookingLink = (packageId, form) => {
  const q = new URLSearchParams({ package: packageId, vehicle: WIZARD_VEHICLE[form.vehicle] || 'sedan', promo: 'TREAT20' });
  if (form.carSeat === 'yes') q.set('addons', 'car_seat');
  return `/detail/book?${q.toString()}`;
};
// The wizard pre-fills from this sessionStorage key (same one the interior quiz uses).
const prefillWizard = (form) => {
  try {
    sessionStorage.setItem('quiz_prefill', JSON.stringify({ name: form.name.trim(), phone: form.phone.replace(/\D/g, '').slice(-10), email: '' }));
  } catch {}
};

// Call OR text — the customer chooses.
const ContactButtons = ({ size = 'lg' }) => (
  <div className={`grid grid-cols-2 gap-3 ${size === 'lg' ? '' : 'max-w-xs mx-auto'}`}>
    <a href={`sms:${SHOP_PHONE_TEL}`} className={`flex items-center justify-center gap-2 rounded-2xl font-black bg-[#D4AF37] text-black ${size === 'lg' ? 'py-4 text-lg' : 'py-3 text-sm'}`}>
      <MessageSquare size={size === 'lg' ? 20 : 16} /> Text
    </a>
    <a href={`tel:${SHOP_PHONE_TEL}`} className={`flex items-center justify-center gap-2 rounded-2xl font-black border-2 border-[#D4AF37] text-[#D4AF37] ${size === 'lg' ? 'py-4 text-lg' : 'py-3 text-sm'}`}>
      <Phone size={size === 'lg' ? 20 : 16} /> Call
    </a>
  </div>
);

const phoneLooksValid = (value) => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const fieldCls = (bad) =>
  `w-full rounded-xl px-4 py-3.5 bg-[#111114] text-white placeholder-gray-500 border-2 focus:outline-none text-base ${
    bad ? 'border-red-500' : 'border-[#2a2a30] focus:border-[#D4AF37]'
  }`;

// Segmented single-choice buttons (big touch targets for phones)
const Choice = ({ options, value, onChange, invalid, columns = 3 }) => (
  <div className={`grid gap-2 ${columns === 2 ? 'grid-cols-2' : columns === 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
    {options.map((o) => (
      <button
        key={o.id}
        type="button"
        onClick={() => onChange(o.id)}
        aria-pressed={value === o.id}
        className={`py-3.5 px-2 rounded-xl border-2 font-bold text-sm transition-all ${
          value === o.id
            ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
            : `bg-[#111114] text-gray-200 hover:border-[#D4AF37]/60 ${invalid ? 'border-red-500' : 'border-[#2a2a30]'}`
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default function HalloweenPage() {
  const ended = Date.now() > HALLOWEEN_CLOSE.getTime();

  const [form, setForm] = useState({
    name: '', phone: '', vehicle: '', packageInterest: 'unsure', carSeat: 'no', notes: '', website: '', // website = honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState('');
  const [done, setDone] = useState(false);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const errors = {
    name: !form.name.trim(),
    phone: !phoneLooksValid(form.phone),
    vehicle: !form.vehicle,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setServerError('');
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'halloween-lead',
          name: form.name.trim(),
          phone: form.phone.trim(),
          vehicle: form.vehicle,
          packageInterest: form.packageInterest,
          carSeat: form.carSeat === 'yes',
          notes: form.notes.trim(),
          website: form.website,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Something went wrong. Please text Akeem at ${SHOP_PHONE_DISPLAY}.`);
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setServerError(err.message || `Something went wrong. Please text Akeem at ${SHOP_PHONE_DISPLAY}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const Header = () => (
    <div className="text-center pt-8 pb-2">
      {/* Sky-and-clouds logo, cut from the flyer artwork so the page matches what people just scanned */}
      <img
        src="/images/logo-sky-circle.png"
        alt="Monarch Detailing"
        className="h-32 w-32 rounded-full object-cover mx-auto mb-4 border-4"
        style={{ borderColor: GOLD }}
      />
      <p className="font-bold tracking-[0.2em] text-sm" style={{ color: GOLD }}>MONARCH DETAILING</p>
      <p className="text-[11px] tracking-[0.3em] text-gray-300 mt-1">MOBILE INTERIOR DETAILING</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#08080a] text-white">
      <div className="max-w-md mx-auto px-5 pb-16">
        <Header />

        {ended ? (
          /* Offer window closed — never a dead end */
          <div className="text-center mt-8">
            <p className="text-3xl mb-3">🎃</p>
            <h1 className="text-3xl font-black mb-3" style={{ color: GOLD }}>This Halloween offer has ended</h1>
            <p className="text-gray-300 mb-8 leading-relaxed">
              Thanks for stopping by! Our Halloween weekend deal is over, but there's still a current offer — and you can always book any interior detail directly.
            </p>
            <Link to="/promo" className="flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-black font-black text-lg py-4 rounded-2xl mb-3 hover:brightness-110 transition-all">
              See the current offer <ArrowRight size={20} />
            </Link>
            <Link to="/detail" className="block w-full border-2 border-[#D4AF37] text-[#D4AF37] font-bold py-3.5 rounded-2xl hover:bg-[#D4AF37]/10 transition-all">
              View packages & book
            </Link>
            <p className="mt-8 mb-3 text-gray-300 text-sm">Questions? Call or text Akeem · {SHOP_PHONE_DISPLAY}</p>
            <ContactButtons size="sm" />
          </div>
        ) : done ? (
          /* Success — book now with the offer applied, or wait for Akeem */
          <div className="text-center mt-8">
            <p className="text-5xl mb-4">🎃</p>
            <h1 className="text-3xl font-black mb-4" style={{ color: GOLD }}>You're in!</h1>
            <p className="text-lg text-gray-100 leading-relaxed mb-8">
              Akeem will call or text you shortly to lock in your 20% off and get you on the schedule.
            </p>

            <div className="bg-[#111114] border-2 rounded-2xl p-5 mb-8 text-left" style={{ borderColor: GOLD }}>
              <p className="font-black text-white text-lg mb-1">Rather book right now?</p>
              <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                Pick your date and lock it in with a 50% deposit — your <strong className="text-white">20% off</strong>
                {form.carSeat === 'yes' ? <> and <strong className="text-white">50% off the car seat deep clean</strong> are</> : ' is'} already applied.
              </p>
              {WIZARD_PACKAGE[form.packageInterest] ? (
                <>
                  <Link
                    to={bookingLink(WIZARD_PACKAGE[form.packageInterest], form)}
                    onClick={() => prefillWizard(form)}
                    className="flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-black font-black text-lg py-4 rounded-2xl hover:brightness-110 transition-all"
                  >
                    Book {NEEDS.find((n) => n.id === form.packageInterest).title} now <ArrowRight size={20} />
                  </Link>
                  <p className="text-xs text-gray-500 mt-3 mb-2">Or book a different package:</p>
                  <div className="flex flex-wrap gap-2">
                    {NEEDS.filter((n) => WIZARD_PACKAGE[n.id] && n.id !== form.packageInterest).map((n) => (
                      <Link key={n.id} to={bookingLink(WIZARD_PACKAGE[n.id], form)} onClick={() => prefillWizard(form)}
                        className="text-xs font-bold text-[#D4AF37] border border-[#D4AF37]/60 rounded-full px-3 py-1.5 hover:bg-[#D4AF37]/10">
                        {n.title}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  {NEEDS.filter((n) => WIZARD_PACKAGE[n.id]).map((n) => (
                    <Link key={n.id} to={bookingLink(WIZARD_PACKAGE[n.id], form)} onClick={() => prefillWizard(form)}
                      className="flex items-center justify-between gap-3 w-full rounded-xl border-2 border-[#D4AF37]/60 px-4 py-3 hover:bg-[#D4AF37]/10 transition-all">
                      <span>
                        <span className="block font-bold text-white text-sm">Book {n.title}</span>
                        <span className="block text-gray-400 text-xs mt-0.5">{n.desc}</span>
                      </span>
                      <ArrowRight size={18} className="text-[#D4AF37] shrink-0" />
                    </Link>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-500 mt-4">Discount applies when you book by midnight tonight (Sun, Nov 1).</p>
            </div>

            <p className="text-sm text-gray-400 mb-3">Prefer to talk first? Call or text {SHOP_PHONE_DISPLAY}</p>
            <ContactButtons />
          </div>
        ) : (
          <>
            {/* Offer */}
            <div className="text-center mt-6">
              <p className="font-black tracking-wide text-sm uppercase leading-snug" style={{ color: ORANGE }}>
                Trick or treat, parents —<br />this one's for you
              </p>
              <h1 className="text-[2rem] leading-tight font-black mt-4" style={{ color: GOLD }}>
                Halloween Weekend — 20% Off Any Interior Detail
              </h1>
              <p className="mt-4 text-lg font-bold" style={{ color: ORANGE }}>
                + 50% off a car seat deep clean — and yes, they stack.
              </p>
              <p className="mt-4 text-gray-100 font-semibold leading-snug">
                Claim by midnight Sunday, Nov 1 — your appointment can be any future date.
              </p>
              <p className="mt-3 text-sm text-gray-400">
                Interiors only · We come to you in St. Mary's, Charles &amp; Calvert County
              </p>
            </div>

            {/* Lead form */}
            <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5" aria-label="Claim your Halloween offer">
              <p className="text-center text-sm text-gray-400 -mb-1">Quick form — 30 seconds, no account needed.</p>

              {/* Honeypot — hidden from people, bots tend to fill it */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                <label>Website <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set({ website: e.target.value })} /></label>
              </div>

              <div>
                <label htmlFor="hw-name" className="block text-sm font-bold text-gray-200 mb-1.5">Name *</label>
                <input id="hw-name" type="text" autoComplete="name" value={form.name} onChange={(e) => set({ name: e.target.value })}
                  className={fieldCls(showErrors && errors.name)} placeholder="Your name" maxLength={100} />
                {showErrors && errors.name && <p className="text-red-400 text-sm mt-1">Please enter your name.</p>}
              </div>

              <div>
                <label htmlFor="hw-phone" className="block text-sm font-bold text-gray-200 mb-1.5">Phone *</label>
                <input id="hw-phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                  className={fieldCls(showErrors && errors.phone)} placeholder="(301) 555-0123" maxLength={20} />
                {showErrors && errors.phone && <p className="text-red-400 text-sm mt-1">Enter a valid 10-digit phone number so Akeem can reach you.</p>}
              </div>

              <div>
                <p className="block text-sm font-bold text-gray-200 mb-1.5">Vehicle *</p>
                <Choice options={VEHICLES} value={form.vehicle} onChange={(v) => set({ vehicle: v })} invalid={showErrors && errors.vehicle} />
                {showErrors && errors.vehicle && <p className="text-red-400 text-sm mt-1">Pick your vehicle type.</p>}
              </div>

              <div>
                <p className="block text-sm font-bold text-gray-200 mb-1.5">What does your interior need? <span className="font-normal text-gray-500">(optional)</span></p>
                <div className="space-y-2" role="radiogroup">
                  {NEEDS.map((n) => {
                    const on = form.packageInterest === n.id;
                    return (
                      <button key={n.id} type="button" role="radio" aria-checked={on} onClick={() => set({ packageInterest: n.id })}
                        className={`w-full flex items-start gap-3 text-left rounded-xl border-2 px-4 py-3 transition-all ${on ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#2a2a30] bg-[#111114] hover:border-[#D4AF37]/60'}`}>
                        <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${on ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-gray-500'}`}>
                          {on && <Check size={12} className="text-black" strokeWidth={4} />}
                        </span>
                        <span>
                          <span className="block font-bold text-white text-sm">{n.title}</span>
                          <span className="block text-gray-400 text-xs mt-0.5">{n.desc}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="block text-sm font-bold text-gray-200 mb-1.5">Add the car seat deep clean at 50% off ($17.50)?</p>
                <Choice options={[{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }]} value={form.carSeat} onChange={(v) => set({ carSeat: v })} columns={2} />
              </div>

              <div>
                <label htmlFor="hw-notes" className="block text-sm font-bold text-gray-200 mb-1.5">Anything we should know? <span className="font-normal text-gray-500">(optional)</span></label>
                <textarea id="hw-notes" rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })}
                  className={fieldCls(false)} placeholder="Preferred dates, stains, pet hair…" maxLength={1000} />
              </div>

              {serverError && (
                <div role="alert" className="flex items-start gap-2 bg-red-950/60 border border-red-500/60 rounded-xl p-3 text-sm text-red-200">
                  <AlertCircle size={18} className="shrink-0 mt-0.5" /> <span>{serverError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 font-black text-lg py-4 rounded-2xl transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ backgroundColor: GOLD, color: '#000' }}
              >
                {submitting ? (<><Loader2 size={20} className="animate-spin" /> Sending…</>) : 'Claim My 20% Off'}
              </button>
              <p className="text-center text-xs text-gray-500">
                No payment now. Akeem will call or text you to confirm.
              </p>
            </form>

            <p className="text-center text-xs text-gray-500 mt-10">
              Akeem · <a href={`sms:${SHOP_PHONE_TEL}`} className="underline">Text</a> or <a href={`tel:${SHOP_PHONE_TEL}`} className="underline">call</a> {SHOP_PHONE_DISPLAY} · monarchdetailing.art
              <br />
              <Link to="/privacy" className="underline">Privacy Policy</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
